import classNames from "classnames";
import uniqueId from "lodash/uniqueId";
import { Option, OptionOriginal } from "PFTypes/options";
import { useEffect, useRef } from "react";

import css from "./list_item.module.scss";
import { ListItemMultiple } from "./list_item_multiple";
import { ListItemSingle } from "./list_item_single";

type ListItemProps<T> = {
  id: string;
  option: Option<T>;
  selected: boolean;
  highlighted: boolean;
  onClick: VoidFunction;
  multiple?: boolean;
  clipOptionsOverflow?: boolean;
  disabled?: boolean;
};

export type ListItemComponentProps<T> = Pick<
  ListItemProps<T>,
  "option" | "selected" | "id" | "onClick" | "clipOptionsOverflow" | "disabled"
>;

export const ListItem = <T extends OptionOriginal = OptionOriginal>({
  id,
  option,
  selected,
  highlighted,
  onClick,
  multiple,
  clipOptionsOverflow,
  disabled
}: ListItemProps<T>) => {
  const ref = useRef<HTMLLIElement>(null);
  const listItemId = useRef(uniqueId(option.value));

  const ListItemComponent = multiple ? ListItemMultiple : ListItemSingle;

  useEffect(() => {
    if (highlighted) {
      ref.current?.scrollIntoView({ block: "nearest" });
    }
  }, [highlighted]);

  const isSingleOptionClickable = !multiple && !option.disabled && !disabled;

  return (
    <li
      ref={ref}
      id={id}
      role="option"
      aria-selected={selected}
      aria-disabled={option.disabled || disabled}
      className={classNames(css.listItem, {
        [css.highlighted]: highlighted,
        [css.disabled]: option.disabled || disabled,
        [css.clickable]: isSingleOptionClickable
      })}
      title={option.value}
      tabIndex={-1}
      /* LI element handles onClick for single selection item */
      {...(!multiple
        ? {
            "onClick": isSingleOptionClickable ? onClick : undefined,
            "aria-label": option.value
          }
        : {})}
    >
      <ListItemComponent
        id={listItemId.current}
        option={option}
        selected={selected}
        onClick={onClick}
        clipOptionsOverflow={clipOptionsOverflow}
        disabled={option.disabled || disabled}
      />
    </li>
  );
};
